import { useNavigate, useLocation } from 'react-router-dom';
import { Fragment, useEffect, useRef } from 'react';
import { Icon } from '@ng-unifi/ui';
import { isNavItemActive } from '../lib/navActive.js';

export interface DrawerNavItem {
  key: string;
  label: string;
  href: string;
  /** Same section headers the sidebar renders. Without them the phone got a
      flat 14-row list of everything, which is the harder screen to scan on. */
  group?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  items: DrawerNavItem[];
  /**
   * UX-003: omitted for roles that cannot engage lockdown. /admin/lockdown is
   * master-only, so rendering the button for everyone meant a tenant operator
   * or resident pressed it and was silently bounced to the dashboard.
   */
  onLockdown?: () => void;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function NavDrawer({ open, onClose, items, onLockdown }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Close the drawer whenever the route changes.
  // onClose is intentionally omitted from the dep array — fires on navigation only.
  useEffect(() => {
    onClose();
  }, [location.pathname]);

  // UX-023: this declared role="dialog" aria-modal="true" and then honoured
  // none of it — focus was never moved in, Tab walked out into the page behind
  // (which stayed fully reachable), and closing left focus wherever it had
  // landed. Escape was the only part that worked.
  useEffect(() => {
    if (!open) return undefined;
    const active = document.activeElement;
    if (active instanceof HTMLElement) previouslyFocusedRef.current = active;
    panelRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const current = document.activeElement as HTMLElement | null;
      if (!current || !panel.contains(current)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && current === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && current === last) {
        e.preventDefault();
        first.focus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      const target = previouslyFocusedRef.current;
      previouslyFocusedRef.current = null;
      if (target?.focus) queueMicrotask(() => target.focus());
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-on-surface/40 md:hidden"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        className="safe-area-pb fixed bottom-0 right-0 top-0 z-50 flex w-72 max-w-[85vw] flex-col overflow-y-auto bg-surface shadow-xl md:hidden"
      >
        <div className="flex items-center justify-between border-b border-outline-variant px-4 py-5">
          <span className="font-title-md text-title-md font-semibold text-on-surface">Menu</span>
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-full text-on-surface-variant hover:bg-surface-container"
            aria-label="Close menu"
          >
            <Icon name="close" className="text-2xl" />
          </button>
        </div>
        <nav aria-label="All pages" className="flex-1 py-2">
          {items.map((item, idx) => {
            const active = isNavItemActive(location.pathname, item.href);
            const prevGroup = idx === 0 ? undefined : items[idx - 1]?.group;
            const renderHeader = item.group && item.group !== prevGroup;
            return (
              <Fragment key={item.key}>
                {renderHeader ? (
                  <div
                    className="mb-1 mt-3 px-5 text-xs font-semibold uppercase tracking-wide text-on-surface-variant"
                    aria-hidden="true"
                  >
                    {item.group}
                  </div>
                ) : null}
                <button
                  type="button"
                  onClick={() => navigate(item.href)}
                  aria-current={active ? 'page' : undefined}
                  className={`w-full px-5 py-3 text-left font-body-md text-body-md ${
                    active
                      ? 'bg-primary-container/10 font-semibold text-primary'
                      : 'text-on-surface hover:bg-surface-container'
                  }`}
                >
                  {item.label}
                </button>
              </Fragment>
            );
          })}
        </nav>
        <div className="space-y-2 border-t border-outline-variant p-4">
          <button
            type="button"
            onClick={() => navigate('/account/security')}
            className="flex w-full items-center gap-2 rounded-lg px-4 py-2.5 text-left font-body-md text-body-md text-on-surface-variant hover:bg-surface-container hover:text-on-surface"
          >
            <Icon name="shield" className="text-lg" />
            Account security
          </button>
          {onLockdown ? (
            <button
              type="button"
              onClick={onLockdown}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-error-container px-4 py-3 font-label-md text-label-md font-bold text-on-error-container"
            >
              <Icon name="emergency_home" className="text-lg" />
              Emergency Lockdown
            </button>
          ) : null}
        </div>
      </div>
    </>
  );
}
